import { useCallback, useEffect, useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { api, errMsg, useAdminApi } from "@/lib/api";

const EMPTY = { title_en: "", title_ml: "", body_en: "", body_ml: "" };

export default function ArticlesTab() {
  const call = useAdminApi();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);

  const loadList = useCallback(() => api.get("/articles").then((r) => setItems(r.data)), []);
  useEffect(() => {
    loadList();
  }, [loadList]);

  const submit = async () => {
    try {
      await call(editing ? "put" : "post", editing ? `/admin/articles/${editing}` : "/admin/articles", form);
      toast.success("Saved");
      setForm(EMPTY);
      setEditing(null);
      loadList();
    } catch (e) {
      toast.error(errMsg(e, "Add at least an English title and text"));
    }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    await call("delete", `/admin/articles/${id}`);
    loadList();
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="space-y-6" data-testid="admin-articles">
      <div className="grid gap-3 md:grid-cols-2">
        <Input data-testid="article-title-en" placeholder="Title (English)" value={form.title_en} onChange={set("title_en")} className="bg-black/20" />
        <Input data-testid="article-title-ml" placeholder="Title (Malayalam)" value={form.title_ml} onChange={set("title_ml")} className="bg-black/20" />
        <Textarea data-testid="article-body-en" rows={6} placeholder="Text (English)" value={form.body_en} onChange={set("body_en")} className="bg-black/20" />
        <Textarea data-testid="article-body-ml" rows={6} placeholder="Text (Malayalam)" value={form.body_ml} onChange={set("body_ml")} className="bg-black/20" />
      </div>
      <div className="flex gap-2">
        <Button data-testid="save-article-btn" onClick={submit} className="rounded-full bg-emerald-400 text-emerald-950 hover:bg-emerald-300">{editing ? "Update article" : "Add article"}</Button>
        {editing && <Button variant="ghost" onClick={() => { setEditing(null); setForm(EMPTY); }}>Cancel</Button>}
      </div>
      <ul className="divide-y divide-white/5">
        {items.map((a) => (
          <li key={a.id} className="flex items-center justify-between gap-3 py-3" data-testid={`admin-article-${a.id}`}>
            <span className="text-sm">{a.title_en}{a.title_ml && <span className="text-emerald-100/50"> · {a.title_ml}</span>}</span>
            <span className="flex gap-1">
              <Button size="icon" variant="ghost" data-testid={`edit-article-${a.id}`} onClick={() => { setEditing(a.id); setForm({ title_en: a.title_en, title_ml: a.title_ml, body_en: a.body_en, body_ml: a.body_ml }); }}><Pencil className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" data-testid={`delete-article-${a.id}`} onClick={() => remove(a.id)}><Trash2 className="h-4 w-4" /></Button>
            </span>
          </li>
        ))}
        {items.length === 0 && <li className="py-6 text-sm text-emerald-100/60">No articles yet. Patients see a "coming soon" note.</li>}
      </ul>
    </div>
  );
}
